import React, { useEffect, useRef, useState } from "react"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Tabs from "aldoo-ra/Tabs"
import TextField from "aldoo-ra/TextField"
import Input from "aldoo-ra/Input"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import AddMediaDialog from "aldoo-ra/AddMediaDialog"
import { navigate } from "vike/client/router"
import PubSub from "pubsub-js"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import Modal from "aldoo-ra/Modal"
import Navbar from "@components/Navbar"
import { listOrgs } from "aldoo-ra/Organizations/api"
import { listProjects, createProject, updateProject, setProjectPublic, listComponents, deleteComponent, deleteProject } from "../api"
import { useStatusUpdates } from "../realtime"
import ComponentCard from "./ComponentCard"
import ComponentDetail from "./ComponentDetail"
import ComponentEditor from "../Components/ComponentEditor"

function makeSlug(text) {
  return String(text || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64)
}

// Read+resize image file → data URL (max 256x256, PNG)
function readImageAsDataUrl(file, maxDim = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height))
        const w = Math.round(img.width * scale)
        const h = Math.round(img.height * scale)
        const canvas = document.createElement("canvas")
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, w, h)
        resolve(canvas.toDataURL("image/png"))
      }
      img.onerror = reject
      img.src = reader.result
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function stripLang(path) {
  return path.replace(/^\/[a-z]{2}(\/|$)/, "/")
}

function parseProjectsPath() {
  if (typeof window === "undefined") return {}
  const path = stripLang(window.location.pathname)
  const m = path.match(/^\/projects(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?\/?$/)
  const params = new URLSearchParams(window.location.search || "")
  const out = m ? { orgSlug: m[1], projectSlug: m[2], componentId: m[3], action: m[4] } : {}
  const start = params.get("start")
  const end = params.get("end")
  const days = params.get("days")
  if (start && end) out.range = { type: "custom", start, end }
  else if (days) out.range = { type: "preset", days: Number(days) }
  return out
}

function buildProjectsUrl({ orgSlug, projectSlug, componentId, action, range }) {
  let u = "/projects"
  if (orgSlug) u += `/${orgSlug}`
  if (projectSlug) u += `/${projectSlug}`
  if (componentId) u += `/${componentId}`
  if (action) u += `/${action}`
  if (range?.type === "custom" && range.start && range.end) {
    u += `?start=${encodeURIComponent(range.start)}&end=${encodeURIComponent(range.end)}`
  } else if (range?.type === "preset" && range.days) {
    u += `?days=${range.days}`
  }
  return u
}

// Convert DateFilterDropdown value → server params + bar config
function rangeFromDateFilter(df) {
  if (df?.type === "custom" && df.range?.startDate && df.range?.endDate) {
    const start = new Date(df.range.startDate)
    const end = new Date(df.range.endDate)
    const days = Math.max(1, Math.ceil((end - start) / 86400000))
    const granularity = days <= 1 ? "hour" : "day"
    return { from: start.toISOString(), to: end.toISOString(), granularity, days, urlRange: { type: "custom", start: start.toISOString(), end: end.toISOString() } }
  }
  const days = df?.value?.days ?? 90
  const granularity = days <= 1 ? "hour" : "day"
  const now = new Date()
  if (granularity === "hour") {
    // Last N*24h ending now
    const to = now
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
    return { from: from.toISOString(), to: to.toISOString(), granularity, days, urlRange: { type: "preset", days } }
  }
  // Daily: include today as last bar. Span = N calendar days ending today.
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  const startOfFirst = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1), 0, 0, 0, 0)
  return { from: startOfFirst.toISOString(), to: endOfToday.toISOString(), granularity, days, urlRange: { type: "preset", days } }
}

const ProjectsPage = () => {
  const [auth] = PlatformAuth.useAuth()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!mounted) return
    if (auth) return
    const redirect = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/projects"
    navigate("/")
    PubSub.publish("signin", { redirect })
  }, [mounted, auth])

  const [orgs, setOrgs] = useState([])
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [projectData, setProjectData] = useState(null) // full project doc
  const [components, setComponents] = useState([])
  const [tab, setTab] = useState("components")
  const [viewingComponent, setViewingComponent] = useState(null)
  const [action, setAction] = useState(null)
  const [pending, setPending] = useState(() => parseProjectsPath())
  const [addingComponent, setAddingComponent] = useState(false)
  const [addingProject, setAddingProject] = useState(false)
  const [projectSearch, setProjectSearch] = useState("")
  const [newProjectName, setNewProjectName] = useState("")
  const [newProjectSlug, setNewProjectSlug] = useState("")
  const [newProjectDesc, setNewProjectDesc] = useState("")
  const newProjectSlugTouched = useState({ v: false })[0]
  const [err, setErr] = useState(null)

  // Auto-derive slug from name unless user typed in slug field
  useEffect(() => {
    if (!addingProject) return
    if (newProjectSlugTouched.v) return
    setNewProjectSlug(makeSlug(newProjectName))
  }, [newProjectName, addingProject])

  const onCreateProject = async () => {
    setErr(null)
    if (!selectedOrg?.value) return setErr("no_org")
    if (!newProjectName.trim()) return setErr("name_required")
    const r = await createProject({ orgId: selectedOrg.value, name: newProjectName.trim(), slug: newProjectSlug || makeSlug(newProjectName), description: newProjectDesc })
    if (r?.error) return setErr(r.error)
    setAddingProject(false)
    setNewProjectName("")
    setNewProjectSlug("")
    setNewProjectDesc("")
    newProjectSlugTouched.v = false
    // Reload project list + open the new one
    const lp = await listProjects(selectedOrg.value)
    const list = lp?.projects || []
    setProjects(list)
    if (r.project) selectProject(r.project)
  }

  // Date range filter (default 30 days, syncs to URL via ?days= or ?start=&end=)
  const [dateFilter, setDateFilter] = useState(() => {
    const p = parseProjectsPath()
    if (p.range?.type === "custom") return { type: "custom", range: { startDate: p.range.start, endDate: p.range.end } }
    if (p.range?.type === "preset") return { type: "preset", value: { days: p.range.days } }
    return { type: "preset", value: { days: 90 } }
  })

  const pushUrl = (parts) => {
    const urlRange = rangeFromDateFilter(dateFilter).urlRange
    const url = buildProjectsUrl({ ...parts, range: urlRange })
    if (typeof window !== "undefined" && window.location.pathname + window.location.search !== url) {
      window.history.pushState({}, "", url)
    }
  }

  // popstate → re-parse URL into pending, reset selections
  useEffect(() => {
    if (typeof window === "undefined") return
    const onPop = () => {
      const p = parseProjectsPath()
      setPending(p)
      setSelectedOrg((cur) => (p.orgSlug && cur?.slug === p.orgSlug ? cur : null))
      setSelectedProject(null)
      setProjectData(null)
      setComponents([])
      setViewingComponent(null)
      setAction(null)
      if (p.range?.type === "custom") setDateFilter({ type: "custom", range: { startDate: p.range.start, endDate: p.range.end } })
      else if (p.range?.type === "preset") setDateFilter({ type: "preset", value: { days: p.range.days } })
      else setDateFilter({ type: "preset", value: { days: 90 } })
    }
    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [])

  // Details tab state
  const [detailsName, setDetailsName] = useState("")
  const [detailsSlug, setDetailsSlug] = useState("")
  const [detailsDescription, setDetailsDescription] = useState("")
  const [detailsLogo, setDetailsLogo] = useState("")
  const [logoDialogOpen, setLogoDialogOpen] = useState(false)

  // Convert blob → base64 data URL for storage in project.logo
  const projectLogoUploadFn = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve({ result: true, dataUrl: reader.result })
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  const [detailsPublic, setDetailsPublic] = useState(false)
  const [detailsPublicSlug, setDetailsPublicSlug] = useState("")

  // Load orgs — gated by auth
  useEffect(() => {
    if (!mounted || !auth) return
    ;(async () => {
      const r = await listOrgs()
      const list = r?.orgs || r?.organizations || r?.list || []
      setOrgs(list)
      // Hydrate org from URL via slugified name, else first
      const pendOrg = pending.orgSlug && list.find((o) => makeSlug(o.name) === pending.orgSlug)
      const target = pendOrg || (list.length ? list[0] : null)
      if (target && !selectedOrg) {
        setSelectedOrg({ value: String(target._id), label: target.name, slug: makeSlug(target.name) })
      }
    })()
  }, [mounted, auth])

  // Load projects
  useEffect(() => {
    if (!mounted || !auth) return
    if (!selectedOrg?.value) return
    setSelectedProject(null)
    setProjectData(null)
    setComponents([])
    ;(async () => {
      const r = await listProjects(selectedOrg.value)
      const list = r?.projects || []
      setProjects(list)
      // Hydrate project from URL via slug
      if (pending.projectSlug) {
        const p = list.find((x) => x.slug === pending.projectSlug)
        if (p) selectProject(p, { fromUrl: true })
      }
    })()
  }, [selectedOrg?.value, mounted, auth])

  // Select project → load full data + components
  const selectProject = async (p, opts = {}) => {
    setSelectedProject(p)
    setViewingComponent(null)
    setTab("components")
    setProjectData(p)
    setDetailsName(p.name || "")
    setDetailsSlug(p.slug || "")
    setDetailsDescription(p.description || "")
    setDetailsLogo(p.logo || "")
    setDetailsPublic(!!p.public)
    setDetailsPublicSlug(p.publicSlug || "")
    const r = await listComponents(p._id, { withStats: true, ...rangeFromDateFilter(dateFilter), tzOffsetMinutes: -new Date().getTimezoneOffset() })
    const comps = r?.components || []
    setComponents(comps)
    // Hydrate component from URL
    if (opts.fromUrl && pending.componentId) {
      const c = comps.find((x) => String(x._id) === pending.componentId)
      if (c) {
        setViewingComponent(c)
        if (pending.action) setAction(pending.action)
      }
      setPending({})
    } else if (!opts.fromUrl) {
      pushUrl({ orgSlug: selectedOrg?.slug, projectSlug: p.slug })
    }
  }

  const refreshComponents = async () => {
    if (!selectedProject?._id) return
    const r = await listComponents(selectedProject._id, { withStats: true, ...rangeFromDateFilter(dateFilter), tzOffsetMinutes: -new Date().getTimezoneOffset() })
    setComponents(r?.components || [])
  }

  // Live updates — refresh components + project rollups when checks finish.
  // Bursts of check completions are coalesced into one refresh (trailing debounce)
  // so an open tab doesn't re-run the full stats load once per check.
  const liveRefreshTimer = useRef(null)
  useEffect(() => () => clearTimeout(liveRefreshTimer.current), [])
  useStatusUpdates({
    projectId: selectedProject?._id,
    onUpdate: () => {
      if (liveRefreshTimer.current) return
      liveRefreshTimer.current = setTimeout(async () => {
        liveRefreshTimer.current = null
        refreshComponents()
        if (selectedOrg?.value) {
          const r = await listProjects(selectedOrg.value)
          setProjects(r?.projects || [])
        }
      }, 2000)
    },
  })

  // Re-fetch + push URL when date range changes
  useEffect(() => {
    if (!selectedProject?._id) return
    refreshComponents()
    pushUrl({ orgSlug: selectedOrg?.slug, projectSlug: selectedProject?.slug, componentId: viewingComponent?._id, action })
  }, [JSON.stringify(dateFilter)])

  const onDeleteComponent = async (c) => {
    const ok = await new Promise((resolve) => {
      showMessageDialog({
        title: "Delete component",
        message: `Delete component "${c.name}"? This cannot be undone.`,
        btnLabel: "Delete",
        onConfirm: () => resolve(true),
        onClose: () => resolve(false),
      })
    })
    if (!ok) return
    const r = await deleteComponent(c._id)
    if (r?.error) return setErr(r.error)
    refreshComponents()
  }

  const applyProjectUpdate = (updated) => {
    if (!updated) return
    setProjectData(updated)
    setSelectedProject(updated)
    setProjects((list) => list.map((p) => (p._id === updated._id ? updated : p)))
    setDetailsName(updated.name || "")
    setDetailsSlug(updated.slug || "")
    setDetailsDescription(updated.description || "")
    setDetailsLogo(updated.logo || "")
    setDetailsPublic(!!updated.public)
    setDetailsPublicSlug(updated.publicSlug || "")
    // Reflect slug change in URL
    pushUrl({ orgSlug: selectedOrg?.slug, projectSlug: updated.slug })
  }

  const onSaveDetails = async () => {
    setErr(null)
    const r = await updateProject({ projectId: selectedProject._id, name: detailsName, slug: detailsSlug, description: detailsDescription, logo: detailsLogo })
    if (r?.error) return setErr(r.error)
    applyProjectUpdate(r.project)
  }

  const onTogglePublic = async (v) => {
    setDetailsPublic(v)
    if (!selectedProject?._id) return
    const r = await setProjectPublic({ projectId: selectedProject._id, public: v, publicSlug: v ? (detailsPublicSlug || detailsSlug) : undefined })
    if (r?.error) return setErr(r.error)
    applyProjectUpdate(r.project)
  }

  const onDeleteProject = () => {
    if (!selectedProject?._id) return
    const name = projectData?.name || selectedProject.name
    showPromptDialog({
      title: "Delete project",
      message: `Delete project "${name}"? This permanently removes all its components, checks, incidents and history. This cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setErr(null)
        const r = await deleteProject(selectedProject._id)
        if (r?.error) return setErr(r.error)
        // Drop from list and reset selection
        setProjects((list) => list.filter((p) => p._id !== selectedProject._id))
        setSelectedProject(null)
        setProjectData(null)
        setComponents([])
        setViewingComponent(null)
        setAction(null)
        setTab("components")
        pushUrl({ orgSlug: selectedOrg?.slug })
      },
    })
  }

  const orgOptions = orgs.map((o) => ({ value: String(o._id), label: o.name }))

  const tabs = [
    { value: "components", label: "Components" },
    { value: "details", label: "Details" },
  ]

  // Auth gate — render nothing until mounted AND authed (no data, no UI flash)
  if (!mounted || !auth) return null

  // Component detail view
  if (viewingComponent) {
    return (
      <div>
        <Navbar />
        <div className="mt-16 p-6 max-w-5xl mx-auto">
          <button
            className="text-link text-sm mb-4"
            onClick={() => {
              setViewingComponent(null)
              setAction(null)
              pushUrl({ orgSlug: selectedOrg?.slug, projectSlug: selectedProject?.slug })
            }}
          >← Back to components</button>
          <ComponentDetail
            component={viewingComponent}
            action={action}
            onAction={(a) => {
              setAction(a)
              pushUrl({ orgSlug: selectedOrg?.slug, projectSlug: selectedProject?.slug, componentId: viewingComponent?._id, action: a })
            }}
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
            onDelete={async () => {
              await onDeleteComponent(viewingComponent)
              setViewingComponent(null)
              setAction(null)
              pushUrl({ orgSlug: selectedOrg?.slug, projectSlug: selectedProject?.slug })
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      <Navbar />

      <Modal
        isOpen={addingProject}
        onClose={() => setAddingProject(false)}
        title="New project"
        variant="compact"
        className="max-w-md"
        closeOnClickOutside
        closeOnEscape
      >
        <div className="p-4 flex flex-col gap-3">
          <TextField label="Name" value={newProjectName} onChange={setNewProjectName} />
          <TextField
            label="Slug"
            value={newProjectSlug}
            onChange={(v) => { newProjectSlugTouched.v = true; setNewProjectSlug(makeSlug(v)) }}
          />
          <TextField label="Description (optional)" value={newProjectDesc} onChange={setNewProjectDesc} collapsible />
          <div className="flex flex-col items-center gap-2">
            <Button onClick={onCreateProject} className="px-10 py-3 text-base">Create</Button>
            {err && <div className="text-error text-sm">Error: {err}</div>}
          </div>
        </div>
      </Modal>

      <div className="flex mt-16" style={{ height: "calc(100vh - 64px)" }}>
        {/* Left sidebar */}
        <div className="w-64 shrink-0 border-r border-bordered bg-paper overflow-y-auto">
          <div className="p-3 border-b border-bordered">
            <div className="text-xs text-text_secondary mb-1">Organization</div>
            <DropdownSelect
              value={selectedOrg}
              onChange={(v) => {
                const next = v?.[0] || v
                const org = orgs.find((o) => String(o._id) === next?.value)
                const withSlug = next && org ? { ...next, slug: makeSlug(org.name) } : next
                setSelectedOrg(withSlug)
                if (withSlug?.slug) pushUrl({ orgSlug: withSlug.slug })
              }}
              options={orgOptions}
            />
          </div>
          <div className="p-3">
            <div className="flex items-center justify-between mb-2 gap-2">
              <span className="text-xs text-text_secondary">Projects</span>
              <Button
                onClick={() => setAddingProject(true)}
                disabled={!selectedOrg?.value}
                className="whitespace-nowrap text-xs px-3 py-1"
              >
                + Project
              </Button>
            </div>
            <div className="mb-2">
              <Input
                value={projectSearch}
                onChange={(e) => setProjectSearch(e?.target?.value ?? "")}
                placeholder="Search projects..."
                showClearButton
                onClear={() => setProjectSearch("")}
              />
            </div>
            {projects.length === 0 && <div className="text-xs text-text_secondary">No projects</div>}
            <div className="space-y-0.5">
              {projects
                .filter((p) => {
                  const q = projectSearch.trim().toLowerCase()
                  if (!q) return true
                  return (p.name || "").toLowerCase().includes(q) || (p.slug || "").toLowerCase().includes(q)
                })
                .map((p, idx) => {
                const statusColor =
                  p.status === "operational" ? "bg-green-500" :
                  p.status === "degraded" ? "bg-amber-500" :
                  p.status === "down" ? "bg-red-500" :
                  null
                const statusLabel =
                  p.status === "operational" ? "All operational" :
                  p.status === "degraded" ? "Degraded" :
                  p.status === "down" ? "Down" : "No data"
                return (
                  <React.Fragment key={p._id}>
                    {idx > 0 && <div className="border-t border-bordered my-0.5" />}
                    <button
                      onClick={() => selectProject(p)}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex items-center gap-2 ${
                        selectedProject?._id === p._id
                          ? "bg-primary/10 text-primary font-medium"
                          : "hover:bg-canvas text-text"
                      }`}
                    >
                      {p.logo ? (
                        <img src={p.logo} alt="" className="w-7 h-7 rounded-md object-cover shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-md bg-neutral_1 flex items-center justify-center text-xs text-text_secondary shrink-0 uppercase">
                          {(p.name || "?").slice(0, 1)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{p.name}</div>
                        <div className="text-xs text-text_secondary truncate">{p.slug}</div>
                      </div>
                      {statusColor && (
                        <span className={`shrink-0 rounded-full h-2.5 w-2.5 ${statusColor}`} title={statusLabel} />
                      )}
                    </button>
                  </React.Fragment>
                )
              })}
            </div>
          </div>
        </div>

        {/* Main area */}
        <div className="flex-1 overflow-y-auto p-6">
          {!selectedOrg && <div className="text-text_secondary">Select an organization.</div>}
          {selectedOrg && !selectedProject && <div className="text-text_secondary">Select a project from the left.</div>}

          {selectedProject && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <Typography variant="admin_h2">{projectData?.name || selectedProject.name}</Typography>
                {tab === "components" && (
                  <div className="flex items-center gap-3">
                    <DateFilterDropdown value={dateFilter} onChange={setDateFilter} showLabel={false} />
                    <Button onClick={() => setAddingComponent(true)} className="whitespace-nowrap">+ Add Component</Button>
                  </div>
                )}
              </div>

              <Tabs tabs={tabs} value={tab} onChange={setTab} />

              {tab === "components" && (
                <div className="space-y-3">
                  <Modal
                    isOpen={addingComponent}
                    onClose={() => { setAddingComponent(false); refreshComponents() }}
                    title="New component"
                    variant="compact"
                    className="max-w-md"
                    closeOnClickOutside
                    closeOnEscape
                  >
                    <div className="p-4">
                      <ComponentEditor
                        projectId={selectedProject._id}
                        onClose={() => { setAddingComponent(false); refreshComponents() }}
                      />
                    </div>
                  </Modal>

                  {components.length === 0 && <div className="text-text_secondary text-sm">No components yet.</div>}
                  <div className="grid gap-3">
                    {components.map((c) => (
                      <ComponentCard
                        key={c._id}
                        component={c}
                        onOpen={() => {
                          setViewingComponent(c)
                          setAction(null)
                          pushUrl({ orgSlug: selectedOrg?.slug, projectSlug: selectedProject?.slug, componentId: c._id })
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {tab === "details" && (
                <div className="max-w-2xl space-y-4">
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setLogoDialogOpen(true)}
                      className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 group border-2 border-dashed border-bordered hover:border-primary transition-colors"
                    >
                      {detailsLogo ? (
                        <img src={detailsLogo} alt="logo" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-canvas text-text_secondary text-xs text-center">
                          Add<br/>logo
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-text_inv text-xs">
                        Edit
                      </div>
                    </button>
                    {detailsLogo && (
                      <button
                        type="button"
                        onClick={() => setDetailsLogo("")}
                        className="text-xs text-error hover:underline"
                      >Remove logo</button>
                    )}
                  </div>

                  {logoDialogOpen && (
                    <AddMediaDialog
                      imagesOnly
                      currentImageUrl={detailsLogo || null}
                      uploadFn={projectLogoUploadFn}
                      onUse={(result) => {
                        if (result?.dataUrl) setDetailsLogo(result.dataUrl)
                        setLogoDialogOpen(false)
                      }}
                      onCancel={() => setLogoDialogOpen(false)}
                    />
                  )}
                  <TextField label="Name" value={detailsName} onChange={setDetailsName} />
                  <TextField label="Slug" value={detailsSlug} onChange={setDetailsSlug} />
                  <TextField label="Description" value={detailsDescription} onChange={setDetailsDescription} />
                  <Button onClick={onSaveDetails}>Save</Button>

                  <div className="border-t border-bordered pt-4 mt-4 space-y-3">
                    <Typography variant="admin_h4">Public status page</Typography>
                    <div className="flex items-center gap-3">
                      <ToggleSwitch isOn={detailsPublic} onChange={onTogglePublic} />
                      <TextField label="Public slug" value={detailsPublicSlug} onChange={setDetailsPublicSlug} disabled={!detailsPublic} />
                    </div>
                  </div>

                  <div className="border-t border-error/30 pt-4 mt-4 space-y-3">
                    <Typography variant="admin_h4" className="text-error">Danger zone</Typography>
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm text-text_secondary">
                        Permanently delete this project and all its components, checks, incidents and history.
                      </div>
                      <Button variant="secondary_red" onClick={onDeleteProject} className="whitespace-nowrap shrink-0">
                        Delete project
                      </Button>
                    </div>
                  </div>
                  {err && <div className="text-error text-sm">Error: {err}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProjectsPage
